import { useSyncExternalStore } from 'react';
import { DARK_PLAYER_THEMES, type PlayerThemeId } from './theme.config';

/**
 * Player layout: how the app is laid out, independent of the colour palette.
 *
 * `classic` is the app as it has always looked. `modern` is the card-based
 * mobile look (rounded match cards, pill chips, a balance pill in the header,
 * Home/Sports/Live/Casino/My bets in the bottom bar, a floating slip button).
 * Both layouts take their colours from the palette the player picked.
 * `vip` is the classic app on a phone in the look of the classic home: dark
 * or light by the player's switch, in every palette, cards, a slip button in the middle of the footer. It
 * works as classic does, so everywhere but its own branches it is not
 * `modern`; a wide screen stays classic, the look is drawn for phones.
 *
 * The choice lives on the device (localStorage) rather than on the account.
 * The legacy `tottalo` palette always stays classic: its PNG artwork is drawn
 * for that look only.
 */
export const PLAYER_LAYOUT_IDS = ['classic', 'modern', 'vip'] as const;
export type PlayerLayoutId = (typeof PLAYER_LAYOUT_IDS)[number];

export const DEFAULT_PLAYER_LAYOUT: PlayerLayoutId = 'classic';

/** From this width on the app shows its desktop pages (useBreakpoint). */
const DESKTOP_WIDTH = 1050;

const STORAGE_KEY = 'tottalo.layout';
const VIP_MODE_KEY = 'tottalo.vipMode';
const CHANGE_EVENT = 'tottalo:layout-changed';

export function normalizePlayerLayout(raw?: string | null): PlayerLayoutId {
  return raw && (PLAYER_LAYOUT_IDS as readonly string[]).includes(raw)
    ? (raw as PlayerLayoutId)
    : DEFAULT_PLAYER_LAYOUT;
}

export function readStoredPlayerLayout(): PlayerLayoutId {
  try {
    return normalizePlayerLayout(localStorage.getItem(STORAGE_KEY));
  } catch {
    return DEFAULT_PLAYER_LAYOUT;
  }
}

/** The layout actually in effect, given the palette on <html>. */
export function effectivePlayerLayout(
  stored: PlayerLayoutId,
  theme: string | undefined,
  width: number = window.innerWidth
): PlayerLayoutId {
  if (theme === 'tottalo') return 'classic';
  if (stored === 'vip' && width >= DESKTOP_WIDTH) return 'classic';
  return stored;
}

/** VIP's background. The player's choice holds for every colour; until there
 *  is one, Claude is light, as it always was, and the others dark. */
export type VipMode = 'dark' | 'light';

export function readVipMode(theme: string | undefined): VipMode {
  try {
    const stored = localStorage.getItem(VIP_MODE_KEY);
    if (stored === 'dark' || stored === 'light') return stored;
  } catch {
    // Private mode: fall through to the palette's own.
  }
  return theme === 'claude' ? 'light' : 'dark';
}

export function setVipMode(mode: VipMode) {
  try {
    localStorage.setItem(VIP_MODE_KEY, mode);
  } catch {
    // Private mode: the choice still applies for this page load.
  }
  applyPlayerLayout();
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

export function applyPlayerLayout(): PlayerLayoutId {
  const root = document.documentElement;
  const layout = effectivePlayerLayout(
    readStoredPlayerLayout(),
    root.dataset.playerTheme
  );
  root.dataset.layout = layout;
  // Classic and modern take the palette's own surface (Claude stays light);
  // VIP's is the player's switch.
  if (layout === 'vip') {
    const mode = readVipMode(root.dataset.playerTheme);
    root.dataset.vipMode = mode;
    root.dataset.themeSurface = mode;
    return layout;
  }
  delete root.dataset.vipMode;
  root.dataset.themeSurface = DARK_PLAYER_THEMES.includes(
    root.dataset.playerTheme as PlayerThemeId
  )
    ? 'dark'
    : 'light';
  return layout;
}

export function setPlayerLayout(layout: PlayerLayoutId) {
  try {
    localStorage.setItem(STORAGE_KEY, layout);
  } catch {
    // Private mode: the choice still applies for this page load.
  }
  applyPlayerLayout();
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

function subscribe(onChange: () => void) {
  // VIP depends on the width: a turned tablet can cross the desktop line.
  const onResize = () => {
    applyPlayerLayout();
    onChange();
  };
  window.addEventListener(CHANGE_EVENT, onChange);
  window.addEventListener('resize', onResize);
  return () => {
    window.removeEventListener(CHANGE_EVENT, onChange);
    window.removeEventListener('resize', onResize);
  };
}

function snapshot(): PlayerLayoutId {
  return normalizePlayerLayout(document.documentElement.dataset.layout);
}

const vipModeSnapshot = () =>
  (document.documentElement.dataset.vipMode as VipMode | undefined) ?? 'dark';

/** VIP's background; re-renders when the player switches it. */
export function useVipMode(): VipMode {
  return useSyncExternalStore(subscribe, vipModeSnapshot, () => 'dark');
}

/** The layout in effect; re-renders when the player switches it. */
export function usePlayerLayout(): PlayerLayoutId {
  return useSyncExternalStore(subscribe, snapshot, () => DEFAULT_PLAYER_LAYOUT);
}
